import { Link } from "react-router-dom";
import { useEffect } from "react";
import {
  Truck,
  Package,
  Calendar,
  Wallet,
  Eye,
  MapPin,
  ArrowRight,
  ShoppingBag,
  CheckCircle2,
} from "lucide-react";
import CustomButton from "./customButton";
import userOrderUsers from "../store/order/getOrderUser";
import Loader from "./loader";

const RecapCommande = ({ t }: any) => {
  const { orderUser, loadingOrderUser, fetchAllOrderUser } = userOrderUsers();

  const selectedStatus = "en-transit";
  const filteredOrders = orderUser.filter(
    (order: any) => order.status === selectedStatus
  );

  useEffect(() => {
    fetchAllOrderUser();
    // eslint-disable-next-line
  }, []);

  // ============ ÉTAT DE CHARGEMENT ============
  if (loadingOrderUser) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden animate-pulse"
          >
            <div className="h-16 bg-gray-100" />
            <div className="p-5 space-y-3">
              <div className="h-4 bg-gray-100 rounded w-1/3" />
              <div className="h-12 bg-gray-100 rounded" />
              <div className="h-12 bg-gray-100 rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ============ ÉTAT VIDE ============
  if (filteredOrders.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 md:p-14 text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-blue-50 to-cyan-100 mb-4">
          <Truck className="w-10 h-10 text-blue-500" />
        </div>
        <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-2">
          {t("commande_transi_non_dispo")}
        </h3>
        <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
          Vos commandes en cours de transit apparaîtront ici dès qu'un
          transporteur les aura prises en charge.
        </p>
        <Link
          to="/list_products"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold rounded-full hover:shadow-lg transition-all hover:scale-105"
        >
          <ShoppingBag className="w-4 h-4" />
          Découvrir les produits
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  // ============ LISTE DES COMMANDES ============
  return (
    <div className="space-y-4 md:space-y-5">
      {/* Header avec compteur */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">
            {filteredOrders.length}
          </span>
          <span className="text-sm text-gray-600 font-medium">
            commande{filteredOrders.length > 1 ? "s" : ""} en transit
          </span>
        </div>
      </div>

      {/* Cartes de commande */}
      {filteredOrders.map((order: any) => (
        <div
          key={order.order_id}
          className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
        >
          {/* ===== HEADER DE LA CARTE ===== */}
          <div className="relative bg-gradient-to-r from-blue-50 to-white px-5 py-4 border-b border-gray-100">
            <div className="flex flex-wrap items-center justify-between gap-3">
              {/* Info principale */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                  <Truck className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">
                    {t("no_commande")}
                  </p>
                  <p className="text-sm font-bold text-blue-600">
                    #{order.order_id}
                  </p>
                </div>
              </div>

              {/* Badge statut */}
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-200">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                {t("wetting_transit")}
              </span>
            </div>
          </div>

          {/* ===== TIMELINE DU TRAJET (si données dispo) ===== */}
          {(order.depart || order.destination) && (
            <div className="px-5 pt-5 pb-1">
              <div className="relative flex items-center gap-3">
                {/* Point départ */}
                <div className="flex flex-col items-center shrink-0">
                  <div className="w-3 h-3 rounded-full bg-emerald-500 ring-4 ring-emerald-100" />
                </div>

                {/* Ligne */}
                <div className="flex-1 relative">
                  <div className="h-0.5 bg-gradient-to-r from-emerald-400 via-blue-400 to-indigo-400 rounded-full" />
                  {/* Petit truck sur la ligne */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-1">
                    <Truck className="w-4 h-4 text-blue-500" />
                  </div>
                </div>

                {/* Point arrivée */}
                <div className="flex flex-col items-center shrink-0">
                  <div className="w-3 h-3 rounded-full bg-indigo-500 ring-4 ring-indigo-100" />
                </div>
              </div>

              {/* Labels villes */}
              <div className="flex items-center justify-between mt-2 text-xs">
                <span className="font-semibold text-gray-700 truncate max-w-[40%]">
                  {order.depart || "—"}
                </span>
                <span className="font-semibold text-gray-700 truncate max-w-[40%] text-right">
                  {order.destination || "—"}
                </span>
              </div>
            </div>
          )}

          {/* ===== MÉTADONNÉES ===== */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 px-5 py-4 bg-gray-50/50 border-y border-gray-100">
            <MetaItem
              icon={<Calendar className="w-4 h-4 text-gray-400" />}
              label={t("commande_le")}
              value={new Date(order.created_at).toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            />
            <MetaItem
              icon={<Package className="w-4 h-4 text-gray-400" />}
              label={t("produit")}
              value={`${order.products.length}`}
            />
            <MetaItem
              icon={<Wallet className="w-4 h-4 text-gray-400" />}
              label={t("prix_total")}
              value={`${parseFloat(order.total_amount).toLocaleString()} F`}
              valueClass="text-emerald-600 font-bold"
              className="col-span-2 md:col-span-1"
            />
          </div>

          {/* ===== CONTENU : PRODUITS + ACTIONS ===== */}
          <div className="p-5 flex flex-col lg:flex-row gap-5">
            {/* Liste des produits */}
            <div className="flex-1 space-y-3">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-blue-500" />
                {t("produit")} ({order.products.length})
              </h3>

              <div className="space-y-2">
                {order.products.map((product: any, index: number) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    {/* Image placeholder */}
                    <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center shrink-0">
                      <Package className="w-6 h-6 text-gray-500" />
                    </div>

                    {/* Infos produit */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">
                        {product.product_name}
                      </p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-gray-500">
                          {t("quantite")}: {product.quantity} sacs
                        </span>
                      </div>
                    </div>

                    {/* Prix */}
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-gray-900">
                        {parseFloat(product.subtotal).toLocaleString()} F
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="lg:w-56 flex lg:flex-col gap-2 flex-row">
              <Link
                to={`/details_commandes/${order.order_id}`}
                className="flex-1 lg:flex-none"
              >
                <button
                  type="button"
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-blue-200 text-blue-600 text-sm font-semibold rounded-xl hover:bg-blue-50 hover:border-blue-300 transition-all"
                >
                  <Eye className="w-4 h-4" />
                  {t("voir_detail_commande")}
                </button>
              </Link>

              {/* Optionnel : bouton "Suivre le colis" (si tu as une page de tracking) */}
              {/* <button
                type="button"
                onClick={() => navigate(`/suivi/${order.order_id}`)}
                className="flex-1 lg:flex-none w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm font-bold rounded-xl shadow-md hover:shadow-lg hover:scale-[1.02] transition-all"
              >
                <MapPin className="w-4 h-4" />
                Suivre le colis
              </button> */}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============ SOUS-COMPOSANT META ============
const MetaItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  valueClass?: string;
  className?: string;
}> = ({ icon, label, value, valueClass = "", className = "" }) => (
  <div className={`flex items-start gap-2 ${className}`}>
    <div className="mt-0.5">{icon}</div>
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
        {label}
      </p>
      <p className={`text-sm font-medium text-gray-800 truncate ${valueClass}`}>
        {value}
      </p>
    </div>
  </div>
);

export default RecapCommande;